import { deleteRequestAsync, getRequestAsync, postRequestAsync } from './apiHandler';

// WhatsApp Agent Management APIs
export const whatsappAgentAPI = {
  // Get manager's agents
  getManagerAgents: async (accessToken) => {
    return await getRequestAsync('/v2/whatsapp/manager/agents', {
      headers: { Authorization: accessToken },
    });
  },

  // Invite new agent
  inviteAgent: async (email, wabaId, accessToken) => {
    return await postRequestAsync(
      '/v2/whatsapp/manager/invite-agent',
      {
        email,
        wabaId,
      },
      accessToken
    );
  },

  // Assign agent directly (without invitation)
  assignAgent: async (agentId, wabaId, accessToken) => {
    return await postRequestAsync(
      '/v2/whatsapp/manager/assign-agent',
      {
        agentId,
        wabaId,
      },
      accessToken
    );
  },

  // Unassign agent
  unassignAgent: async (agentId, accessToken) => {
    return await deleteRequestAsync(`/v2/whatsapp/manager/unassign-agent/${agentId}`, {
      headers: { Authorization: accessToken },
    });
  },

  // Cancel agent invitation
  cancelInvitation: async (agentId, accessToken) => {
    return await deleteRequestAsync(`/v2/whatsapp/manager/cancel-invitation/${agentId}`, {
      headers: { Authorization: accessToken },
    });
  },

  // Accept agent invitation (public endpoint)
  acceptInvitation: async (token, accessToken) => {
    return await postRequestAsync(`/v2/whatsapp/agent/accept-invitation/${token}`, {}, accessToken);
  },

  // Reject agent invitation (public endpoint)
  rejectInvitation: async (token, accessToken) => {
    return await postRequestAsync(`/v2/whatsapp/agent/reject-invitation/${token}`, {}, accessToken);
  },

  // Create handover note
  createNote: async (conversationId, noteText, agent, accessToken) => {
    return await postRequestAsync(
      `/v2/whatsapp/agent/conversations/${conversationId}/notes`,
      {
        noteText,
        agent,
      },
      accessToken
    );
  },

  // AI Assist message generation
  aiAssist: async (conversationId, text, tone, agent, accessToken) => {
    return await postRequestAsync(
      `/v2/whatsapp/agent/conversations/${conversationId}/ai-assist`,
      {
        text,
        tone,
        agent,
      },
      accessToken
    );
  },

  // Close conversation
  closeConversation: async (conversationId, accessToken, userRole = 'agent') => {
    const endpoint =
      userRole === 'manager'
        ? `/v2/whatsapp/manager/conversations/${conversationId}/close`
        : `/v2/whatsapp/agent/conversations/${conversationId}/close`;

    return await postRequestAsync(endpoint, {}, accessToken);
  },

  // Open conversation
  openConversation: async (conversationId, accessToken, userRole = 'agent') => {
    const endpoint =
      userRole === 'manager'
        ? `/v2/whatsapp/manager/conversations/${conversationId}/open`
        : `/v2/whatsapp/agent/conversations/${conversationId}/open`;

    return await postRequestAsync(endpoint, {}, accessToken);
  },

  // Check if conversation can be reopened
  canReopenConversation: async (conversationId, accessToken, userRole = 'agent') => {
    const endpoint =
      userRole === 'manager'
        ? `/v2/whatsapp/manager/conversations/${conversationId}/can-reopen`
        : `/v2/whatsapp/agent/conversations/${conversationId}/can-reopen`;

    return await getRequestAsync(endpoint, {
      headers: { Authorization: accessToken },
    });
  },
};

// WhatsApp Billing Analytics APIs
export const whatsappBillingAPI = {
  // Get billing analytics data
  getBillingAnalytics: async (wabaId, phoneNumberId, params, accessToken) => {
    const queryParams = new URLSearchParams(params).toString();
    return await getRequestAsync(
      `/v2/whatsapp/${wabaId}/${phoneNumberId}/billing-analytics?${queryParams}`,
      {
        headers: { Authorization: accessToken },
      }
    );
  },

  // Export billing data
  exportBillingData: async (wabaId, phoneNumberId, params, accessToken) => {
    const queryParams = new URLSearchParams(params).toString();
    return await getRequestAsync(
      `/v2/whatsapp/${wabaId}/${phoneNumberId}/export-billing?${queryParams}`,
      {
        headers: { Authorization: accessToken },
      }
    );
  },
};

// WhatsApp Conversation Management APIs
export const whatsappConversationAPI = {
  // Get manager conversations with filters
  getManagerConversations: async (filters, accessToken) => {
    const queryParams = new URLSearchParams();

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.show) queryParams.append('show', filters.show);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.agent_id) queryParams.append('agent_id', filters.agent_id);
    if (filters.waba_id) queryParams.append('waba_id', filters.waba_id);
    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.offset) queryParams.append('offset', filters.offset);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/v2/whatsapp/manager/conversations?${queryString}`
      : '/v2/whatsapp/manager/conversations';

    return await getRequestAsync(endpoint, {
      headers: { Authorization: accessToken },
    });
  },

  // Get agent conversations with filters
  getAgentConversations: async (filters, accessToken) => {
    const queryParams = new URLSearchParams();

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.show) queryParams.append('show', filters.show);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.waba_id) queryParams.append('waba_id', filters.waba_id);
    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.offset) queryParams.append('offset', filters.offset);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/v2/whatsapp/agent/conversations?${queryString}`
      : '/v2/whatsapp/agent/conversations';

    return await getRequestAsync(endpoint, {
      headers: { Authorization: accessToken },
    });
  },

  // Assign conversation to agent
  assignConversation: async (conversationId, agentId, accessToken) => {
    return await postRequestAsync(
      '/v2/whatsapp/manager/assign-conversation',
      {
        conversation_id: conversationId,
        agent_user_id: agentId,
      },
      accessToken
    );
  },

  // Unassign conversation
  unassignConversation: async (conversationId, accessToken) => {
    return await postRequestAsync(
      '/v2/whatsapp/manager/unassign-conversation',
      {
        conversation_id: conversationId,
      },
      accessToken
    );
  },

  // Transfer conversation between agents
  transferConversation: async (conversationId, fromAgentId, toAgentId, reason, accessToken) => {
    return await postRequestAsync(
      '/v2/whatsapp/manager/transfer-conversation',
      {
        conversation_id: conversationId,
        from_agent_id: fromAgentId,
        to_agent_id: toAgentId,
        reason,
      },
      accessToken
    );
  },
};
